import React from 'react';
import { Routes, Route, HashRouter } from 'react-router-dom';
import { GlobalHistory } from '@app/controllers/navigationController';

// no lazy loading for auth pages to avoid flickering
const AuthLayout = React.lazy(() => import('@app/components/layouts/AuthLayout/AuthLayout'));
import LoginPage from '@app/pages/LoginPage';
import NewPasswordPage from '@app/pages/NewPasswordPage';

import MainLayout from '@app/components/layouts/main/MainLayout/MainLayout';
import RequireAuth from '@app/components/router/RequireAuth';
import { withLoading } from '@app/hocs/withLoading.hoc';
import DashboardPage from '@app/pages/DashboardPages/DashboardPage';
import ProfileLayout from '@app/components/profile/ProfileLayout';
import InvalidRoutePage from '@app/pages/InvalidRoutePage';
import {
  CreateUser,
  EditUser,
  EmployeeDatatable,
  FirmDatatable,
  FirmEdit,
  FirmRegister,
  Logout,
  PersonalInfoPage,
  SecuritySettingsPage,
  UserDatatable,
  EmployeeRegister,
  EmployeeEdit,
  Attendence,
  Salary,
  SalaryReport,
  EmployeeSafety,
  EmployeeSafetyRegister,
  EmployeeSafetyEdit,
  SafetyReport,
  Fileroom,
  EmployeeAdvance,
  EmployeeAdvanceRegister,
  EmployeeAdvanceEdit,
  AdvanceReport,
  AdttendenceeReport,
} from './LazyRoutes';

export const DASHBOARD_PATH = '/';

// Profile
const PersonalInfo = withLoading(PersonalInfoPage);
const SecuritySettings = withLoading(SecuritySettingsPage);
const UserDatatableWithLoading = withLoading(UserDatatable);
const CreateUserWithLoading = withLoading(CreateUser);
const EditUserWithLoading = withLoading(EditUser);
const EmployeeDatatableWithLoading = withLoading(EmployeeDatatable);
const FirmDatatableWithLoading = withLoading(FirmDatatable);
const FirmRegisterWithLoading = withLoading(FirmRegister);
const FirmEditWithLoading = withLoading(FirmEdit);
const MedicalDashboard = withLoading(DashboardPage);
const AuthLayoutFallback = withLoading(AuthLayout);
const LogoutFallback = withLoading(Logout);
const EmployeeRegisterWithLoading = withLoading(EmployeeRegister);
const EmployeeEditWithLoading = withLoading(EmployeeEdit);
const AttendenceWithLoading = withLoading(Attendence);
const SalaryWithLoading = withLoading(Salary);
const SalaryReportWithLoading = withLoading(SalaryReport);
const EmployeeSafetyWithLoading = withLoading(EmployeeSafety);
const EmployeeSafetyRegisterWithLoading = withLoading(EmployeeSafetyRegister);
const EmployeeSafetyEditWithLoading = withLoading(EmployeeSafetyEdit);
const EmployeeSafetyReportWithLoading = withLoading(SafetyReport);
const FileRoomWithLoading = withLoading(Fileroom);
const EmployeeAdvanceLoading = withLoading(EmployeeAdvance);
const EmployeeAdvanceRegisterLoading = withLoading(EmployeeAdvanceRegister);
const EmployeeAdvanceEditLoading = withLoading(EmployeeAdvanceEdit);
const AdvanceReportLoading = withLoading(AdvanceReport);
const AdttendenceeReportLoading = withLoading(AdttendenceeReport);

export const AppRouter: React.FC = () => {
  const protectedLayout = (
    <RequireAuth>
      <MainLayout />
    </RequireAuth>
  );

  return (
    <HashRouter>
      <GlobalHistory />
      <Routes>
        <Route path={DASHBOARD_PATH} element={protectedLayout}>
          <Route index element={<MedicalDashboard />} />
          <Route path="profile" element={<ProfileLayout />}>
            <Route path="personal-info" element={<PersonalInfo />} />
            <Route path="security-settings" element={<SecuritySettings />} />
          </Route>
          <Route path="firm" element={<FirmDatatableWithLoading />} />
          <Route path="firm/register" element={<FirmRegisterWithLoading />} />
          <Route path="firm/edit" element={<FirmEditWithLoading />} />

          <Route path="user" element={<UserDatatableWithLoading />} />
          <Route path="user/register" element={<CreateUserWithLoading />} />
          <Route path="user/edit" element={<EditUserWithLoading />} />

          <Route path="employee" element={<EmployeeDatatableWithLoading />} />
          <Route path="employee/register" element={<EmployeeRegisterWithLoading />} />
          <Route path="employee/edit" element={<EmployeeEditWithLoading />} />

          <Route path="employeesafety" element={<EmployeeSafetyWithLoading />} />
          <Route path="employeesafety/register" element={<EmployeeSafetyRegisterWithLoading />} />
          <Route path="employeesafety/edit" element={<EmployeeSafetyEditWithLoading />} />

          <Route path="timesheet" element={<AttendenceWithLoading />} />
          <Route path="employeesalary" element={<SalaryWithLoading />} />

          <Route path="employeesalaryreport" element={<SalaryReportWithLoading />} />
          <Route path="employeesafetyreport" element={<EmployeeSafetyReportWithLoading />} />
          <Route path="employeeadvancereport" element={<AdvanceReportLoading />} />
          <Route path="employeeattendencereport" element={<AdttendenceeReportLoading />} />

          <Route path="fileroom" element={<FileRoomWithLoading />} />

          <Route path="employeeadvance" element={<EmployeeAdvanceLoading />} />
          <Route path="employeeadvance/register" element={<EmployeeAdvanceRegisterLoading />} />
          <Route path="employeeadvance/edit" element={<EmployeeAdvanceEditLoading />} />
        </Route>
        <Route path="/auth" element={<AuthLayoutFallback />}>
          <Route path="login" element={<LoginPage />} />
          <Route path="new-password" element={<NewPasswordPage />} />
        </Route>
        <Route path="/error" element={<AuthLayoutFallback />}>
          <Route path="invalidroute" element={<InvalidRoutePage />} />
        </Route>
        <Route path="/logout" element={<LogoutFallback />} />
      </Routes>
    </HashRouter>
  );
};
